import { router, usePathname } from "expo-router";
import { LayoutGrid, PhoneCall, UserCircle } from "lucide-react-native";
import React, { useCallback, type ComponentType } from "react";
import { Platform, Pressable, StyleSheet, Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

export const PUBLIC_BOTTOM_BAR_CONTENT_HEIGHT = 64;
export const PUBLIC_BOTTOM_BAR_BOTTOM_GAP = 10;

const ACTIVE_COLOR = "#2563eb";
const INACTIVE_COLOR = "#6b7280";

type PublicBottomTab = {
  label: string;
  href: "/directory" | "/profile" | "/contact";
  activePaths: string[];
  Icon: ComponentType<{
    color?: string;
    size?: number | string;
    strokeWidth?: number | string;
  }>;
};

const TABS: PublicBottomTab[] = [
  {
    label: "Directory",
    href: "/directory",
    activePaths: ["/", "/directory"],
    Icon: LayoutGrid,
  },
  {
    label: "Profile",
    href: "/profile",
    activePaths: ["/profile"],
    Icon: UserCircle,
  },
  {
    label: "Contact",
    href: "/contact",
    activePaths: ["/contact"],
    Icon: PhoneCall,
  },
];

const PUBLIC_BOTTOM_BAR_PATHS = new Set(
  TABS.flatMap((item) => item.activePaths)
);

function getViewportBottomInset(bottomInset: number) {
  return Platform.OS === "web" ? 0 : bottomInset;
}

export function getPublicBottomBarPadding(bottomInset: number) {
  const viewportBottomInset = getViewportBottomInset(bottomInset);

  return (
    PUBLIC_BOTTOM_BAR_CONTENT_HEIGHT +
    Math.max(viewportBottomInset, PUBLIC_BOTTOM_BAR_BOTTOM_GAP) +
    24
  );
}

export default function PublicBottomBar() {
  const pathname = usePathname();
  const insets = useSafeAreaInsets();

  const openTab = useCallback(
    (href: PublicBottomTab["href"], active: boolean) => {
      if (active) return;

      router.replace(href as any);
    },
    []
  );

  if (!PUBLIC_BOTTOM_BAR_PATHS.has(pathname)) {
    return null;
  }

  const bottomPadding = Math.max(
    getViewportBottomInset(insets.bottom),
    PUBLIC_BOTTOM_BAR_BOTTOM_GAP
  );

  return (
    <View
      pointerEvents="box-none"
      style={[styles.wrap, { paddingBottom: bottomPadding }]}
    >
      <View style={styles.bar}>
        {TABS.map(({ label, href, activePaths, Icon }) => {
          const active = activePaths.includes(pathname);
          const color = active ? ACTIVE_COLOR : INACTIVE_COLOR;

          return (
            <Pressable
              key={href}
              onPress={() => openTab(href, active)}
              accessibilityRole="tab"
              accessibilityState={{ selected: active }}
              accessibilityLabel={label}
              style={({ pressed }) => [
                styles.tab,
                pressed ? styles.tabPressed : null,
              ]}
            >
              <Icon color={color} size={23} strokeWidth={2.4} />
              <Text style={[styles.label, { color }]} numberOfLines={1}>
                {label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 50,
    elevation: 12,
    backgroundColor: "#ffffff",
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
    shadowColor: "#000000",
    shadowOpacity: 0.08,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: -6 },
  },
  bar: {
    height: PUBLIC_BOTTOM_BAR_CONTENT_HEIGHT,
    width: "100%",
    maxWidth: 640,
    alignSelf: "center",
    flexDirection: "row",
    alignItems: "stretch",
    justifyContent: "space-around",
    paddingHorizontal: 8,
    paddingTop: 6,
  },
  tab: {
    flex: 1,
    minHeight: 54,
    alignItems: "center",
    justifyContent: "center",
    gap: 3,
  },
  tabPressed: {
    opacity: 0.72,
  },
  label: {
    fontSize: 11,
    lineHeight: 14,
    fontWeight: "900",
  },
});
